/**
 * Einzelne Gebäude-Karte im Tableau-Grid.
 * Zeigt Icon, Name, Level-Badge, Stats und Upgrade-Kosten.
 */
import type { BuildingDef } from '../config/BuildingTypes';
import {
  factoryProduction, factoryEnergy, solarOutput,
  storageCapacity, upgradeCost, upgradeTime,
} from '../systems/BuildingFormulas';
import { formatNumber, formatFull } from '../math/formatNumber';
import { startCountdown, formatTime } from './BuildingCardTimer';
import { isMobile } from './UITheme';

function cardStyle(): string {
  const m = isMobile();
  return 'position:relative;background:rgba(136,204,255,0.04);border:1px solid rgba(136,204,255,0.12);' +
    `border-radius:${m ? 6 : 8}px;padding:${m ? '6px 8px' : 'clamp(10px,2vw,14px)'};font-family:monospace;` +
    `font-size:${m ? '10px' : 'clamp(10px,2.4vw,12px)'};color:#8cf;` +
    `min-height:${m ? '70px' : '110px'};display:flex;flex-direction:column;gap:${m ? '3px' : '6px'};`;
}

function badgeStyle(): string {
  const m = isMobile();
  return `position:absolute;top:0;right:0;min-width:${m ? 22 : 28}px;padding:${m ? '2px 5px' : '4px 8px'};` +
    `font-size:${m ? '13px' : 'clamp(14px,3vw,18px)'};font-weight:bold;color:#bdf;line-height:1;text-align:center;` +
    'background:rgba(136,204,255,0.1);border-left:1px solid rgba(136,204,255,0.15);' +
    `border-bottom:1px solid rgba(136,204,255,0.15);border-radius:0 ${m ? 5 : 7}px 0 ${m ? 4 : 6}px;`;
}

function statStyle(): string {
  return `color:#6a8a9a;font-size:${isMobile() ? '9px' : 'clamp(9px,2.2vw,11px)'};`;
}

function btnStyle(): string {
  const m = isMobile();
  return `margin-top:auto;padding:${m ? '6px 8px' : 'clamp(8px,2vw,10px) clamp(10px,2.5vw,14px)'};` +
    'background:rgba(136,204,255,0.1);border:1px solid rgba(136,204,255,0.2);border-radius:4px;color:#8cf;' +
    `cursor:pointer;font-family:monospace;font-size:${m ? '10px' : 'clamp(10px,2.2vw,11px)'};text-align:left;` +
    `transition:background 0.15s;min-height:${m ? '34px' : '44px'};`;
}


const UNKNOWN_OVERLAY =
  'position:absolute;inset:0;background:rgba(0,0,0,0.5);border-radius:8px;' +
  'display:flex;align-items:center;justify-content:center;' +
  'font-size:20px;color:rgba(255,255,255,0.2);letter-spacing:4px;';

const DOWN_BTN_STYLE =
  'position:absolute;top:0;right:0;margin-right:44px;padding:4px 6px;' +
  'background:rgba(255,100,100,0.1);border:none;border-bottom:1px solid rgba(255,100,100,0.2);' +
  'border-left:1px solid rgba(255,100,100,0.2);border-radius:0 0 0 6px;' +
  'color:#f66;cursor:pointer;font-size:12px;font-family:monospace;line-height:1;';

export interface BuildingCardCallbacks {
  onUpgrade?: (type: string, steps?: number) => void;
  onDowngrade?: (type: string, steps?: number) => void;
  onTimerDone?: (type: string) => void;
}

export function createBuildingCard(
  def: BuildingDef, level: number,
  known: boolean, canUpgrade: boolean,
  cb?: BuildingCardCallbacks,
  canDowngrade = false,
  upgradingUntil?: string | null,
  cleanups?: (() => void)[],
  energyModifier = 1,
): HTMLDivElement {
  const m = isMobile();
  const card = document.createElement('div');
  card.style.cssText = cardStyle();

  // Header: Icon + Name
  const header = document.createElement('div');
  header.style.cssText = `display:flex;align-items:center;gap:${m ? '8px' : '12px'};margin-bottom:${m ? '2px' : '4px'};`;
  const imgSize = m ? '32px' : 'clamp(36px,8vw,48px)';
  const nameSize = m ? '11px' : 'clamp(13px,2.8vw,14px)';
  header.innerHTML = `<img src="/assets/buildings/${def.icon}" alt="${def.name}" style="width:${imgSize};height:${imgSize};border-radius:${m ? 4 : 6}px;object-fit:cover;background:#000;border:1px solid rgba(136,204,255,0.2);">` +
    `<span style="color:#bdf;font-size:${nameSize};font-weight:bold;">${def.name}</span>`;
  card.appendChild(header);

  // Level-Badge oben rechts
  const badge = document.createElement('span');
  badge.style.cssText = badgeStyle();
  badge.textContent = known ? String(level) : '?';
  card.appendChild(badge);

  // Dev: Downgrade-Step-Buttons links vom Badge
  if (canDowngrade && level > 0) {
    const wrap = document.createElement('div');
    wrap.style.cssText = DOWN_BTN_STYLE + 'display:flex;gap:1px;padding:2px 4px;';
    for (const s of [1, 5, 10]) {
      const b = document.createElement('button');
      b.style.cssText = 'background:none;border:none;color:#f66;cursor:pointer;' +
        'font-family:monospace;font-size:11px;padding:2px 4px;';
      b.textContent = `-${s}`;
      b.addEventListener('click', () => cb?.onDowngrade?.(def.type, s));
      wrap.appendChild(b);
    }
    card.appendChild(wrap);
  }

  if (!known) {
    const overlay = document.createElement('div');
    overlay.style.cssText = UNKNOWN_OVERLAY;
    overlay.textContent = '??';
    card.appendChild(overlay);
    return card;
  }

  // Stats (mit Energie-Modifier für effektive Produktion)
  const stats = document.createElement('div');
  stats.style.cssText = statStyle();
  stats.innerHTML = buildStats(def, level, energyModifier);
  card.appendChild(stats);

  // Aktiver Upgrade-Timer
  if (upgradingUntil) {
    const timerEl = document.createElement('div');
    timerEl.style.cssText = 'margin-top:auto;color:#fc6;font-size:11px;font-family:monospace;';
    card.appendChild(timerEl);
    startCountdown(timerEl, upgradingUntil, level, () => cb?.onTimerDone?.(def.type), cleanups);
    return card;
  }

  // Upgrade-Button(s)
  if (canUpgrade && level < def.maxLevel && canDowngrade) {
    // Dev-Modus: kostenlose Step-Buttons + Kosten-Anzeige
    const cost = upgradeCost(level + 1, def.costWeights);
    const costTip = `${formatFull(cost.feronium)}F  ${formatFull(cost.chrysalium)}C  ${formatFull(cost.neurogel)}N`;
    const costEl = document.createElement('div');
    costEl.style.cssText = 'color:#6a8a9a;font-size:10px;margin-top:auto;';
    const time = formatTime(upgradeTime(level + 1));
    costEl.style.cssText += 'display:flex;justify-content:space-between;';
    costEl.innerHTML = `<span>Lv${level + 1}: <span data-tip="${costTip}">${formatNumber(cost.feronium)}F ${formatNumber(cost.chrysalium)}C ${formatNumber(cost.neurogel)}N</span></span><span style="color:#fc6">\u23f1${time}</span>`;
    card.appendChild(costEl);
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:4px;margin-top:4px;';
    for (const s of [1, 5, 10]) {
      const b = document.createElement('button');
      b.style.cssText = btnStyle() + 'flex:1;text-align:center;';
      b.textContent = `+${s}`;
      b.addEventListener('click', () => cb?.onUpgrade?.(def.type, s));
      row.appendChild(b);
    }
    card.appendChild(row);
  } else if (canUpgrade && level < def.maxLevel) {
    const cost = upgradeCost(level + 1, def.costWeights);
    const btn = document.createElement('button');
    btn.style.cssText = btnStyle();
    const costTip = `${formatFull(cost.feronium)}F  ${formatFull(cost.chrysalium)}C  ${formatFull(cost.neurogel)}N`;
    const time = formatTime(upgradeTime(level + 1));
    btn.style.cssText += 'display:flex;justify-content:space-between;align-items:center;';
    btn.innerHTML = `<span>\u25B8 Lv${level + 1} <span style="color:#6a8a9a" title="${costTip}">${formatNumber(cost.feronium)}F ${formatNumber(cost.chrysalium)}C ${formatNumber(cost.neurogel)}N</span></span>` +
      `<span style="color:#fc6">\u23f1${time}</span>`;
    btn.addEventListener('click', () => {
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.innerHTML = '<span>\u2692 Upgrade\u2026</span>';
      cb?.onUpgrade?.(def.type);
    });
    card.appendChild(btn);
  } else if (level >= def.maxLevel) {
    const maxEl = document.createElement('div');
    maxEl.style.cssText = 'margin-top:auto;color:#4a6a5a;font-size:11px;';
    maxEl.textContent = 'MAX LEVEL';
    card.appendChild(maxEl);
  }

  return card;
}

function buildStats(def: BuildingDef, level: number, modifier = 1): string {
  if (level === 0) return 'Inaktiv \u2014 Upgrade auf Lv1 erforderlich';

  if (def.type === 'solar_collector') {
    const output = solarOutput(level, def.baseRate);
    return `Energie: +${output.toFixed(1)}/s`;
  }
  if (def.category === 'factory') {
    const prod = factoryProduction(level, def.baseRate);
    const eff = prod * modifier;
    const energy = factoryEnergy(level, def.baseEnergy);
    const modHint = modifier < 1
      ? ` <span style="color:#f66">\u26A1${Math.round(modifier * 100)}%</span>` : '';
    return `Prod: ${eff.toFixed(1)}/s${modHint} \u00B7 Energie: -${energy.toFixed(1)}/s`;
  }
  // storage
  const cap = storageCapacity(level, def.baseRate);
  const rounded = Math.round(cap);
  return `Kapazit\u00e4t: <span title="${formatFull(rounded, 't')}">${formatNumber(rounded, 't')}</span>`;
}
